import { AlertCircle, Copy } from "lucide-react";
import toast from "react-hot-toast";
import type { SaveErrorState } from "./sessionSaveWithValidation";

export function SaveErrorsBanner({
  saveErrors,
  isSaving,
  onDismiss,
}: {
  saveErrors: NonNullable<SaveErrorState>;
  isSaving: boolean;
  onDismiss: () => void;
}) {
  const inProgress =
    saveErrors.message.includes("Fixing") ||
    saveErrors.message.includes("Validating") ||
    saveErrors.message.includes("Re-validating");
  const success = saveErrors.message.includes("Fixed and saved");
  const bgClass = inProgress
    ? "bg-amber-50 dark:bg-amber-950/30 border-amber-200 dark:border-amber-900/50"
    : success
      ? "bg-green-50 dark:bg-green-950/30 border-green-200 dark:border-green-900/50"
      : "bg-red-50 dark:bg-red-950/30 border-red-200 dark:border-red-900/50";
  const textClass = inProgress
    ? "text-amber-800 dark:text-amber-300"
    : success
      ? "text-green-800 dark:text-green-300"
      : "text-red-800 dark:text-red-300";
  const labelClass = inProgress
    ? "text-amber-700 dark:text-amber-400"
    : success
      ? "text-green-700 dark:text-green-400"
      : "text-red-700 dark:text-red-400";

  if (inProgress || success) {
    return (
      <div
        className={`flex items-center justify-between gap-2 px-4 py-2 border-b shrink-0 ${bgClass}`}
      >
        <div className="flex items-center gap-2 min-w-0 flex-1">
          {inProgress && (
            <div className="h-3 w-3 animate-spin rounded-full border-[1.5px] border-amber-600 border-t-transparent shrink-0" />
          )}
          <span className={`text-xs truncate ${textClass}`}>
            {saveErrors.message}
          </span>
        </div>
        {!isSaving && (
          <button
            type="button"
            onClick={onDismiss}
            className={`text-xs px-2 py-0.5 rounded hover:bg-muted/50 shrink-0 ${labelClass}`}
          >
            Dismiss
          </button>
        )}
      </div>
    );
  }

  return (
    <div className={`px-4 py-3 border-b shrink-0 ${bgClass}`}>
      <div className="flex items-start justify-between gap-3 mb-2">
        <div className="flex items-center gap-2 min-w-0">
          <AlertCircle className={`h-4 w-4 shrink-0 ${labelClass}`} />
          <span
            className={`text-xs font-semibold uppercase tracking-wide ${labelClass}`}
          >
            Compilation error
          </span>
        </div>
        <div className="flex items-center gap-1 shrink-0">
          <button
            type="button"
            onClick={async () => {
              try {
                await navigator.clipboard.writeText(saveErrors.message);
                toast.success("Error copied to clipboard");
              } catch {
                toast.error("Copy failed — select and copy manually");
              }
            }}
            className={`flex items-center gap-1 text-xs px-2 py-1 rounded border border-current hover:bg-muted/50 ${labelClass}`}
            title="Copy error message"
          >
            <Copy className="h-3 w-3" />
            <span>Copy</span>
          </button>
          {!isSaving && (
            <button
              type="button"
              onClick={onDismiss}
              className={`text-xs px-2 py-1 rounded hover:bg-muted/50 ${labelClass}`}
            >
              Dismiss
            </button>
          )}
        </div>
      </div>
      <pre
        className={`text-xs font-mono whitespace-pre-wrap break-words max-h-40 overflow-y-auto select-text rounded bg-background/40 border border-current/20 p-2 ${textClass}`}
      >
        {saveErrors.message}
      </pre>
    </div>
  );
}
